import React, { FC, useMemo } from 'react';

import { Typography } from '@mui/material';

import styled from '../../../../styled';
import { LARGE_MARGIN_PX, SMALL_MARGIN, SMALL_MARGIN_PX } from '../../../../theme';
import { SendHitsEvent } from '../../const';
import { GlossaryGridHit } from '../GlossaryGridHit';
import { Hit } from '../Hit';

type HitsObj = {
  [x: string]: any[];
};

const generateHitsWithLetters = (hits, tenantTitle, sendEvent) => {
  if (hits.length > 0) {
    const res: JSX.Element[] = [];
    const lettersArr: string[] = [];

    hits.forEach((hit) => {
      if (hit?.letter_index && !lettersArr.includes(hit.letter_index)) {
        lettersArr.push(hit.letter_index);
        res.push(
          <Letter key={hit.letter_index} className={'hit-letter'}>
            {hit.letter_index}
          </Letter>,
        );
      }

      res.push(
        <li key={hit.objectID}>
          <Hit hit={hit} tenantTitle={tenantTitle} sendEvent={sendEvent} />
        </li>,
      );
    });

    return res;
  } else {
    return null;
  }
};

const normalizeHits = (hits): HitsObj => {
  const obj = {};

  hits.forEach((hit) => {
    if (hit?.letter_index && hit.letter_index in obj) {
      obj[hit.letter_index].push(hit);
    } else {
      obj[hit.letter_index] = [hit];
    }
  });

  return obj;
};

interface GlossaryInfiniteHitsProps {
  hits: any[];
  tenantTitle?: string | null;
  isGridView?: boolean;
  sendEvent: SendHitsEvent;
}

const GlossaryInfiniteHits: FC<GlossaryInfiniteHitsProps> = ({
  hits,
  tenantTitle,
  isGridView = false,
  sendEvent,
}) => {
  const normalizedHits = useMemo(() => {
    if (isGridView && Array.isArray(hits)) {
      return normalizeHits(hits);
    } else {
      return null;
    }
  }, [hits, isGridView]);

  if (isGridView) {
    return (
      <>
        {normalizedHits &&
          Object.keys(normalizedHits).map((key) => (
            <LetterBlock key={`index-${key}`}>
              <Letter as={Typography}>{key}</Letter>
              <HitsBlock>
                {normalizedHits[key]?.map((hit) => (
                  <GlossaryGridHit key={hit?.id} hit={hit} sendEvent={sendEvent} />
                ))}
              </HitsBlock>
            </LetterBlock>
          ))}
      </>
    );
  }

  return <>{generateHitsWithLetters(hits, tenantTitle, sendEvent)}</>;
};

export default GlossaryInfiniteHits;

const Letter = styled('li', {
  shouldForwardProp: (prop) => prop !== 'as',
})`
  font-weight: bold;
  font-size: 32px;
  line-height: 38px;
  border-bottom: 3px solid ${({ theme }) => theme.palette.common.violetAnalyticsCard};
  padding-bottom: ${SMALL_MARGIN / 2}px;

  ${({ theme }) => theme.breakpoints.up('md')} {
    font-size: 40px;
    line-height: 48px;
    margin-bottom: ${LARGE_MARGIN_PX};
  }
`;
const LetterBlock = styled('div')`
  width: 100%;
`;
const HitsBlock = styled('div')`
  margin-bottom: ${LARGE_MARGIN_PX};
  display: flex;
  flex-wrap: wrap;
  margin-right: -${SMALL_MARGIN_PX};
  width: 100%;

  & > div {
    width: calc((100% - ${SMALL_MARGIN * 3}px) / 3);
    margin-right: ${SMALL_MARGIN_PX};
  }
`;
